import { User as FirebaseAuthUser, getAuth } from 'firebase/auth';
import { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { Route, RouteComponentProps, Switch, match, matchPath, withRouter } from 'react-router-dom';
import { Loader } from '../../components/LoaderComponent';
import { Errors } from '../../helpers/Errors';
import { AppLayout } from '../../layouts/App/AppLayout';
import { AuthLayout } from '../../layouts/Auth/AuthLayout';
import { Routes } from '../../routes/Routes';
import { appLayoutRoutes } from '../../routes/RoutesSelection';
import { EmailVerifiedModal } from '../../screens/Auth/EmailVerifiedModal';
import { NotFoundScreen } from '../../screens/Auth/NotFoundScreen';
import { AuthStoreAction, AuthStoreState } from '../../store/AuthStore';
import { store } from '../../store/Store';
import { ActionType, ConnectInitializeUser } from '../../store/StoreTypes';
import { AppStackInitializer } from '../AppStackInitializer';

/**
 * Wrap the Navigator in a component that acts as a gate. Allows all screens within the AppStack to assume
 * that that user is available, the AppStore view of the Redux store is fully initialized etc.
 */

type IAppStackState = {
  initialised: boolean;
};

type Props = {
  hcpReferralHash: string | undefined;
  firebaseUser: FirebaseAuthUser | undefined;
  history: History;
  linkedPatientCount: number;
  setHcpReferralHash: (hcpReferralHash: string | undefined) => void;
};

class AppStackComp extends Component<RouteComponentProps & Props, IAppStackState> {
  state: IAppStackState = {
    initialised: false,
  };

  async componentDidMount() {
    const { setHcpReferralHash, location, history } = this.props;
    const userIdMatch: match<{ uid: string }> | null = matchPath(history.location.pathname, {
      path: '/patient/:uid',
    });
    if (!userIdMatch?.isExact) {
      await this.initialiseAppStack();
    }

    if (userIdMatch?.isExact) {
      setHcpReferralHash(userIdMatch?.params.uid);
      sessionStorage.setItem('hcpReferralHash', userIdMatch?.params.uid ?? '');
      history.push('/users/patient/consent');
    }
    if (location.pathname.includes('auth/')) {
      history.push('/');
    }

    this.setState({ initialised: true });
  }

  initialiseAppStack = async () => {
    const { history } = this.props;
    try {
      await new AppStackInitializer(store).initialize();
    } catch (error) {
      Errors.capture(error, 'warning');
      await getAuth().signOut();
      this.setState({ initialised: true });
      history.push('/auth/sign-in-or-register');
    }
  };

  render() {
    const { initialised } = this.state;
    return initialised ? (
      <>
        <Switch data-testid="app-stack">
          {Routes(AppLayout, appLayoutRoutes, this.props.linkedPatientCount)}
          <Route
            path="*"
            render={(props: RouteComponentProps) => (
              <AuthLayout>
                <NotFoundScreen {...props} />
              </AuthLayout>
            )}
          />
        </Switch>
        <EmailVerifiedModal />
      </>
    ) : (
      <Loader />
    );
  }
}

function mapStateToProps(state: AuthStoreState): {
  hcpReferralHash: string | undefined;
  firebaseUser: FirebaseAuthUser | undefined;
  linkedPatientCount: number;
} {
  const user = state.user as ConnectInitializeUser;
  return {
    hcpReferralHash: state.hcpReferralHash,
    firebaseUser: state.firebaseUser,
    linkedPatientCount: user?.linkedPatients?.length ?? 0,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AuthStoreAction>) {
  return {
    setHcpReferralHash: (hcpReferralHash: string | undefined) =>
      dispatch({ type: ActionType.SET_HCP_PATIENT_LINK_ID, hcpReferralHash }),
  };
}

export const AppStack = withRouter(connect(mapStateToProps, mapDispatchToProps)(AppStackComp));
